import type { Invoice } from '@/types/invoices';
import type {
  InvoiceEquipmentResolution,
  InvoiceLocationProvisioningInput,
  InvoiceLocationProvisioningResult,
  InvoicePaidEquipmentItem,
  InvoicePaidEquipmentPlan,
  InvoicePaidEquipmentResult,
  InvoicePaidWorkItem,
  InvoicePaidWorkItemEvent,
  InvoicePaidWorkRoute,
} from '@/types/invoice-paid-work';

const mockWorkItems: InvoicePaidWorkItem[] = [];
const mockEvents: InvoicePaidWorkItemEvent[] = [];
let sequence = 0;

function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${Date.now()}-${sequence}`;
}

function now(): string {
  return new Date().toISOString();
}

function addEvent(
  workItem: InvoicePaidWorkItem,
  eventType: string,
  metadata: Record<string, unknown> = {}
): void {
  mockEvents.push({
    id: nextId('invoice-paid-event'),
    work_item_id: workItem.id,
    invoice_id: workItem.invoice_id,
    event_type: eventType,
    metadata,
    created_at: now(),
    created_by: null,
  });
}

async function resolveAssociations(invoice: Invoice): Promise<{
  state: 'none' | 'one_location' | 'conflict';
  locationId: string | null;
  sources: string[];
  orderState: 'none' | 'explicit' | 'single' | 'ambiguous' | 'not_applicable';
  orderId: string | null;
}> {
  const [{ mockDealsService }, { mockQuotesService }, { mockLocationOrdersService }] = await Promise.all([
    import('./mock-deals'),
    import('./mock-quotes'),
    import('./mock-location-orders'),
  ]);
  const candidates: Array<{ locationId: string; source: string }> = [];

  if (invoice.location_id) candidates.push({ locationId: invoice.location_id, source: 'invoice' });

  if (invoice.location_order_id) {
    const order = await mockLocationOrdersService.getById(invoice.location_order_id);
    if (order) candidates.push({ locationId: order.location_id, source: 'location_order' });
  }

  if (invoice.quote_id) {
    const quote = await mockQuotesService.getById(invoice.quote_id);
    if (quote?.location_id) candidates.push({ locationId: quote.location_id, source: 'quote' });
  }

  if (invoice.deal_id) {
    const deal = await mockDealsService.getById(invoice.deal_id);
    if (deal?.location_id) candidates.push({ locationId: deal.location_id, source: 'deal' });
  }

  const locationIds = Array.from(new Set(candidates.map((candidate) => candidate.locationId)));
  const sources = Array.from(new Set(candidates.map((candidate) => candidate.source))).sort();

  if (locationIds.length === 0) {
    return { state: 'none', locationId: null, sources, orderState: 'none', orderId: null };
  }
  if (locationIds.length > 1) {
    return { state: 'conflict', locationId: null, sources, orderState: 'not_applicable', orderId: null };
  }

  const locationId = locationIds[0] ?? null;
  if (!locationId) {
    return { state: 'none', locationId: null, sources, orderState: 'none', orderId: null };
  }
  if (invoice.location_order_id) {
    return {
      state: 'one_location',
      locationId,
      sources,
      orderState: 'explicit',
      orderId: invoice.location_order_id,
    };
  }

  const orders = await mockLocationOrdersService.getByLocationId(locationId);
  if (orders.length === 0) {
    return { state: 'one_location', locationId, sources, orderState: 'none', orderId: null };
  }
  if (orders.length === 1) {
    return { state: 'one_location', locationId, sources, orderState: 'single', orderId: orders[0]?.id ?? null };
  }
  return { state: 'one_location', locationId, sources, orderState: 'ambiguous', orderId: null };
}

function updateWorkItem(workItem: InvoicePaidWorkItem, update: Partial<InvoicePaidWorkItem>): void {
  Object.assign(workItem, update, { updated_at: now() });
}

export async function syncMockInvoicePaidWork(
  invoice: Invoice,
  previousInvoice: Invoice | null = null
): Promise<void> {
  const existing = mockWorkItems.find((workItem) => workItem.invoice_id === invoice.id) ?? null;

  if (invoice.status !== 'paid') {
    if (previousInvoice?.status === 'paid') {
      const workItem = existing ?? {
        id: nextId('invoice-paid-work'),
        invoice_id: invoice.id,
        route: 'association_review',
        status: 'cancelled',
        payment_review_required: true,
        payment_reversed_at: now(),
        deal_id: invoice.deal_id,
        source_customer_name: invoice.customer_name.trim(),
        resolved_location_id: null,
        resolved_location_order_id: null,
        association_sources: [],
        resolution_code: 'payment_reversed',
        attempt_count: 0,
        last_error_code: null,
        last_error_message: null,
        created_at: now(),
        updated_at: now(),
        completed_at: null,
        created_by: null,
        updated_by: null,
      } satisfies InvoicePaidWorkItem;
      if (!existing) mockWorkItems.push(workItem);
      updateWorkItem(workItem, {
        status: workItem.status === 'completed' ? 'completed' : 'cancelled',
        payment_review_required: true,
        payment_reversed_at: now(),
        deal_id: invoice.deal_id,
        source_customer_name: invoice.customer_name.trim(),
        resolution_code: workItem.status === 'completed' ? workItem.resolution_code : 'payment_reversed',
      });
      addEvent(workItem, 'payment_reversal_review_created', {
        from_status: previousInvoice.status,
        to_status: invoice.status,
        work_status: workItem.status,
      });
    }
    return;
  }

  if (existing?.status === 'processing') return;

  const resolution = await resolveAssociations(invoice);
  const linkedDeal = invoice.deal_id
    ? await (await import('./mock-deals')).mockDealsService.getById(invoice.deal_id)
    : null;
  const linkedDealEligible = !invoice.deal_id || Boolean(
    linkedDeal
      && !linkedDeal.location_id
      && linkedDeal.stage !== 'closed_lost'
  );
  let route: InvoicePaidWorkRoute | null = null;
  let resolutionCode = 'no_paid_action';

  if (resolution.state === 'conflict') {
    route = 'association_review';
    resolutionCode = 'location_association_conflict';
  } else if (resolution.state === 'one_location' && resolution.orderState === 'ambiguous') {
    route = 'association_review';
    resolutionCode = 'location_order_ambiguous';
  } else if (resolution.state === 'one_location') {
    route = 'existing_location_equipment';
    resolutionCode = 'existing_location_resolved';
  } else if (invoice.provisions_new_location && invoice.customer_name.trim() && linkedDealEligible) {
    route = 'new_location';
    resolutionCode = 'new_location_requested';
  } else if (invoice.provisions_new_location && invoice.deal_id) {
    route = 'association_review';
    resolutionCode = 'deal_not_eligible';
  } else if (invoice.provisions_new_location) {
    route = 'association_review';
    resolutionCode = 'missing_customer_name';
  }

  if (existing?.status === 'completed') {
    const associationChanged = !route
      || route === 'association_review'
      || existing.resolved_location_id !== resolution.locationId
      || existing.resolved_location_order_id !== resolution.orderId;
    updateWorkItem(existing, {
      payment_review_required: associationChanged,
      payment_reversed_at: associationChanged ? existing.payment_reversed_at : null,
      deal_id: invoice.deal_id,
      source_customer_name: invoice.customer_name.trim(),
      association_sources: resolution.sources,
    });
    addEvent(existing, associationChanged ? 'association_change_review_required' : 'completed_work_payment_observed', {
      route,
      resolution_code: resolutionCode,
      association_state: resolution.state,
      order_state: resolution.orderState,
      work_status: existing.status,
    });
    return;
  }

  if (!route) {
    if (existing) {
      updateWorkItem(existing, {
        status: 'cancelled',
        payment_review_required: false,
        payment_reversed_at: null,
        deal_id: invoice.deal_id,
        source_customer_name: invoice.customer_name.trim(),
        resolution_code: resolutionCode,
        last_error_code: null,
        last_error_message: null,
      });
      addEvent(existing, previousInvoice?.status !== 'paid' ? 'payment_restored_no_action' : 'work_cancelled_no_action');
    }
    return;
  }

  const workItem = existing ?? {
    id: nextId('invoice-paid-work'),
    invoice_id: invoice.id,
    route,
    status: 'pending',
    payment_review_required: false,
    payment_reversed_at: null,
    deal_id: invoice.deal_id,
    source_customer_name: invoice.customer_name.trim(),
    resolved_location_id: resolution.locationId,
    resolved_location_order_id: resolution.orderId,
    association_sources: resolution.sources,
    resolution_code: resolutionCode,
    attempt_count: 0,
    last_error_code: null,
    last_error_message: null,
    created_at: now(),
    updated_at: now(),
    completed_at: null,
    created_by: null,
    updated_by: null,
  } satisfies InvoicePaidWorkItem;

  if (!existing) {
    mockWorkItems.push(workItem);
  } else {
    updateWorkItem(existing, {
      route,
      status: 'pending',
      payment_review_required: false,
      payment_reversed_at: null,
      deal_id: invoice.deal_id,
      source_customer_name: invoice.customer_name.trim(),
      resolved_location_id: resolution.locationId,
      resolved_location_order_id: resolution.orderId,
      association_sources: resolution.sources,
      resolution_code: resolutionCode,
      last_error_code: null,
      last_error_message: null,
      completed_at: null,
    });
  }

  addEvent(workItem, 'payment_routed', {
    route,
    resolution_code: resolutionCode,
    association_state: resolution.state,
    order_state: resolution.orderState,
    work_status: workItem.status,
  });
}

export async function refreshAllMockPaidWorkAssociations(): Promise<void> {
  const { mockInvoicesService } = await import('./mock-invoices');
  const invoices = await mockInvoicesService.getAll();
  for (const invoice of invoices) {
    if (invoice.status === 'paid') {
      await syncMockInvoicePaidWork(invoice, invoice);
    }
  }
}

async function requireWorkItem(workItemId: string, route: InvoicePaidWorkRoute): Promise<InvoicePaidWorkItem> {
  const workItem = mockWorkItems.find((item) => item.id === workItemId);
  if (!workItem) throw new Error('Paid invoice work item was not found.');
  if (workItem.route !== route) throw new Error('Paid invoice work is not ready for this action.');
  if (!['pending', 'failed'].includes(workItem.status) || workItem.payment_review_required) {
    throw new Error('Paid invoice work is not ready for this action.');
  }
  return workItem;
}

async function buildEquipmentPlan(workItem: InvoicePaidWorkItem): Promise<InvoicePaidEquipmentPlan> {
  const [
    { mockInvoicesService },
    { mockLocationOrderItemsService },
    { mockDisplayTypesService },
    { mockCustomDisplayTypesService },
  ] = await Promise.all([
    import('./mock-invoices'),
    import('./mock-location-orders'),
    import('./mock-display-types'),
    import('./mock-custom-display-types'),
  ]);
  const invoice = await mockInvoicesService.getById(workItem.invoice_id);
  if (!invoice) throw new Error('Invoice is no longer available.');

  const [standardTypes, customTypes, existingItems] = await Promise.all([
    mockDisplayTypesService.getAll(),
    mockCustomDisplayTypesService.getAll(),
    workItem.resolved_location_order_id
      ? mockLocationOrderItemsService.getByOrderId(workItem.resolved_location_order_id)
      : Promise.resolve([]),
  ]);
  const labels = new Map<string, string>([
    ...standardTypes.map((item) => [item.id, item.name] as const),
    ...customTypes.map((item) => [item.id, item.name] as const),
  ]);
  const quantities = new Map<string, number>();
  let skipped = 0;

  for (const lineItem of invoice.line_items) {
    if (!lineItem.display_type_id || !labels.has(lineItem.display_type_id) || lineItem.quantity <= 0) {
      skipped += 1;
      continue;
    }
    quantities.set(
      lineItem.display_type_id,
      (quantities.get(lineItem.display_type_id) ?? 0) + lineItem.quantity
    );
  }

  const items: InvoicePaidEquipmentItem[] = Array.from(quantities.entries()).map(([displayTypeId, quantity]) => {
    const existingItem = existingItems.find((item) => item.display_type_id === displayTypeId) ?? null;
    return {
      display_type_id: displayTypeId,
      display_type_name: labels.get(displayTypeId) ?? displayTypeId,
      invoice_quantity: quantity,
      existing_item_id: existingItem?.id ?? null,
      current_quantity: existingItem?.quantity ?? null,
      combined_quantity: (existingItem?.quantity ?? 0) + quantity,
      conflict: Boolean(existingItem),
    };
  });

  return {
    ok: true,
    error_code: null,
    error_message: null,
    work_item_id: workItem.id,
    location_id: workItem.resolved_location_id,
    location_order_id: workItem.resolved_location_order_id,
    skipped_non_eligible_count: skipped,
    items,
  };
}

export const mockInvoicePaidWorkService = {
  async getAll(): Promise<InvoicePaidWorkItem[]> {
    return [...mockWorkItems].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  },

  async getByInvoiceId(invoiceId: string): Promise<InvoicePaidWorkItem | null> {
    return mockWorkItems.find((workItem) => workItem.invoice_id === invoiceId) ?? null;
  },

  async getByInvoiceIds(invoiceIds: string[]): Promise<InvoicePaidWorkItem[]> {
    const requestedIds = new Set(invoiceIds);
    return mockWorkItems.filter((workItem) => requestedIds.has(workItem.invoice_id));
  },

  async getEvents(workItemId: string): Promise<InvoicePaidWorkItemEvent[]> {
    return mockEvents.filter((event) => event.work_item_id === workItemId);
  },

  async getLocationOrdersForReview(locationId: string) {
    const { mockLocationOrdersService } = await import('./mock-location-orders');
    return mockLocationOrdersService.getByLocationId(locationId);
  },

  async completeNewLocation(
    input: InvoiceLocationProvisioningInput
  ): Promise<InvoiceLocationProvisioningResult> {
    const workItem = await requireWorkItem(input.workItemId, 'new_location');
    const [
      { mockInvoicesService },
      { mockLocationsService },
      { mockLocationOrdersService, mockLocationOrderItemsService },
      { mockDealsService },
    ] = await Promise.all([
      import('./mock-invoices'),
      import('./mock-locations'),
      import('./mock-location-orders'),
      import('./mock-deals'),
    ]);
    const invoice = await mockInvoicesService.getById(workItem.invoice_id);
    if (!invoice || invoice.status !== 'paid' || !invoice.provisions_new_location) {
      throw new Error('Invoice is no longer paid or marked to create a location.');
    }
    if (invoice.customer_name.trim() !== workItem.source_customer_name || invoice.deal_id !== workItem.deal_id) {
      throw new Error('Invoice source details changed. Refresh before retrying.');
    }
    const duplicate = (await mockLocationsService.getAll()).some(
      (location) => location.location_number.toLowerCase() === input.locationNumber.trim().toLowerCase()
    );
    if (duplicate) throw new Error('Location number already exists. Choose a different number and retry.');

    const linkedDeal = invoice.deal_id ? await mockDealsService.getById(invoice.deal_id) : null;
    if (invoice.deal_id && (
      !linkedDeal
      || linkedDeal.location_id
      || linkedDeal.stage === 'closed_lost'
    )) {
      throw new Error('Linked prospect is no longer eligible for conversion.');
    }

    updateWorkItem(workItem, {
      status: 'processing',
      attempt_count: workItem.attempt_count + 1,
      last_error_code: null,
      last_error_message: null,
    });
    addEvent(workItem, 'attempt_started', { route: 'new_location', attempt_count: workItem.attempt_count });

    let createdLocationId: string | null = null;
    let createdOrderId: string | null = null;
    const createdItemIds: string[] = [];
    let dealUpdated = false;

    try {
      const location = await mockLocationsService.create({
        location_number: input.locationNumber.trim(),
        location_name: input.locationName.trim(),
        status: input.locationStatus,
        address: input.address ?? null,
        contract_signed_date: input.contractSignedDate ?? null,
        contract_renewal_date: input.contractRenewalDate ?? null,
        contract_term_months: input.contractTermMonths ?? null,
        contract_signee_name: input.contractSigneeName ?? null,
        contract_signee_title: input.contractSigneeTitle ?? null,
        installation_date: input.installationDate ?? null,
        launch_date: input.launchDate ?? null,
        monthly_license_fee: input.monthlyLicenseFee ?? null,
        license_fee_period: input.licenseFeePeriod ?? null,
        pos_system: input.posSystem ?? null,
      });
      createdLocationId = location.id;
      const today = now().slice(0, 10);
      const order = await mockLocationOrdersService.create({
        location_id: location.id,
        deal_id: invoice.deal_id,
        order_date: today,
        status: 'paid',
        total_amount: invoice.total,
        paid_amount: invoice.total,
        paid_date: today,
        notes: 'Created from confirmed paid invoice location provisioning',
      });
      createdOrderId = order.id;
      const equipment = new Map<string, { quantity: number; unitPrice: number }>();
      for (const item of invoice.line_items) {
        if (!item.display_type_id || item.quantity <= 0) continue;
        const current = equipment.get(item.display_type_id) ?? { quantity: 0, unitPrice: 0 };
        const nextQuantity = current.quantity + item.quantity;
        equipment.set(item.display_type_id, {
          quantity: nextQuantity,
          unitPrice: nextQuantity > 0
            ? (current.quantity * current.unitPrice + item.quantity * item.unit_price) / nextQuantity
            : 0,
        });
      }
      for (const [displayTypeId, item] of equipment) {
        const createdItem = await mockLocationOrderItemsService.create({
          order_id: order.id,
          display_type_id: displayTypeId,
          quantity: item.quantity,
          unit_price: item.unitPrice,
        });
        createdItemIds.push(createdItem.id);
      }
      if (linkedDeal) {
        await mockDealsService.update(linkedDeal.id, {
          location_id: location.id,
          stage: 'closed_won',
          sales_state: 4,
          prospect_contact_name: null,
          prospect_contact_email: null,
          prospect_contact_phone: null,
          prospect_contact_role: null,
        });
        dealUpdated = true;
      }
      await mockInvoicesService.update(invoice.id, {
        location_id: location.id,
        location_order_id: order.id,
      });
      updateWorkItem(workItem, {
        route: 'new_location',
        status: 'completed',
        payment_review_required: false,
        resolved_location_id: location.id,
        resolved_location_order_id: order.id,
        association_sources: ['invoice'],
        resolution_code: 'location_created',
        completed_at: now(),
      });
      addEvent(workItem, 'location_created', {
        location_id: location.id,
        location_order_id: order.id,
        deal_linked: Boolean(invoice.deal_id),
        attempt_count: workItem.attempt_count,
      });
      return {
        ok: true,
        error_code: null,
        error_message: null,
        work_item_id: workItem.id,
        location_id: location.id,
        location_order_id: order.id,
        attempt_count: workItem.attempt_count,
      };
    } catch (error) {
      if (dealUpdated && linkedDeal) {
        await mockDealsService.update(linkedDeal.id, {
          location_id: linkedDeal.location_id,
          stage: linkedDeal.stage,
          sales_state: linkedDeal.sales_state,
          prospect_contact_name: linkedDeal.prospect_contact_name,
          prospect_contact_email: linkedDeal.prospect_contact_email,
          prospect_contact_phone: linkedDeal.prospect_contact_phone,
          prospect_contact_role: linkedDeal.prospect_contact_role,
        });
      }
      for (const itemId of createdItemIds.reverse()) {
        await mockLocationOrderItemsService.delete(itemId);
      }
      if (createdOrderId) await mockLocationOrdersService.delete(createdOrderId);
      if (createdLocationId) await mockLocationsService.delete(createdLocationId);
      const message = error instanceof Error ? error.message : 'Location conversion failed.';
      updateWorkItem(workItem, {
        status: 'failed',
        last_error_code: 'conversion_failed',
        last_error_message: message,
      });
      addEvent(workItem, 'attempt_failed', { error_code: 'conversion_failed', error_message: message });
      throw error;
    }
  },

  async prepareEquipment(workItemId: string): Promise<InvoicePaidEquipmentPlan> {
    const workItem = await requireWorkItem(workItemId, 'existing_location_equipment');
    return buildEquipmentPlan(workItem);
  },

  async applyEquipment(
    workItemId: string,
    plan: InvoicePaidEquipmentPlan,
    resolutions: Map<string, InvoiceEquipmentResolution>
  ): Promise<InvoicePaidEquipmentResult> {
    const workItem = await requireWorkItem(workItemId, 'existing_location_equipment');
    const [{ mockInvoicesService }, { mockLocationOrdersService, mockLocationOrderItemsService }] = await Promise.all([
      import('./mock-invoices'),
      import('./mock-location-orders'),
    ]);
    const invoice = await mockInvoicesService.getById(workItem.invoice_id);
    if (!invoice || invoice.status !== 'paid' || !workItem.resolved_location_id) {
      throw new Error('Invoice or location association changed. Refresh before retrying.');
    }
    const currentPlan = await buildEquipmentPlan(workItem);
    const conflicts = currentPlan.items.filter((item) => item.conflict);
    if (conflicts.some((item) => !resolutions.has(item.display_type_id))) {
      throw new Error('Choose a resolution for every equipment conflict.');
    }
    if (plan.items.some((item) => {
      const current = currentPlan.items.find((candidate) => candidate.display_type_id === item.display_type_id);
      return !current
        || current.current_quantity !== item.current_quantity
        || current.invoice_quantity !== item.invoice_quantity;
    })) {
      throw new Error('Equipment changed after review. Refresh before retrying.');
    }

    updateWorkItem(workItem, {
      status: 'processing',
      attempt_count: workItem.attempt_count + 1,
      last_error_code: null,
      last_error_message: null,
    });
    addEvent(workItem, 'attempt_started', {
      route: 'existing_location_equipment',
      attempt_count: workItem.attempt_count,
    });

    let orderId = workItem.resolved_location_order_id;
    if (!orderId) {
      const order = await mockLocationOrdersService.create({
        location_id: workItem.resolved_location_id,
        deal_id: invoice.deal_id,
        order_date: now().slice(0, 10),
        status: 'pending',
        notes: 'Created from paid invoice equipment sync',
      });
      orderId = order.id;
    }

    let createdCount = 0;
    let updatedCount = 0;
    let keptCount = 0;
    for (const item of currentPlan.items) {
      if (!item.existing_item_id) {
        await mockLocationOrderItemsService.create({
          order_id: orderId,
          display_type_id: item.display_type_id,
          quantity: item.invoice_quantity,
        });
        createdCount += 1;
        continue;
      }
      const resolution = resolutions.get(item.display_type_id);
      if (resolution === 'keep') {
        keptCount += 1;
      } else if (resolution === 'new' || resolution === 'combine') {
        await mockLocationOrderItemsService.update(item.existing_item_id, {
          quantity: resolution === 'new' ? item.invoice_quantity : item.combined_quantity,
        });
        updatedCount += 1;
      }
    }

    await mockInvoicesService.update(invoice.id, {
      location_id: workItem.resolved_location_id,
      location_order_id: orderId,
    });
    updateWorkItem(workItem, {
      route: 'existing_location_equipment',
      status: 'completed',
      payment_review_required: false,
      resolved_location_order_id: orderId,
      resolution_code: createdCount + updatedCount + keptCount === 0 ? 'no_equipment_lines' : 'equipment_applied',
      completed_at: now(),
    });
    addEvent(workItem, 'equipment_applied', {
      location_id: workItem.resolved_location_id,
      location_order_id: orderId,
      attempt_count: workItem.attempt_count,
      created_count: createdCount,
      updated_count: updatedCount,
      kept_count: keptCount,
      skipped_non_eligible_count: currentPlan.skipped_non_eligible_count,
    });

    return {
      ok: true,
      error_code: null,
      error_message: null,
      work_item_id: workItem.id,
      location_id: workItem.resolved_location_id,
      location_order_id: orderId,
      attempt_count: workItem.attempt_count,
      created_count: createdCount,
      updated_count: updatedCount,
      kept_count: keptCount,
      skipped_non_eligible_count: currentPlan.skipped_non_eligible_count,
    };
  },
};
